import { useState, useEffect, useCallback } from 'react';
import { Split, RefreshCw, TrendingUp, TrendingDown, AlertTriangle } from 'lucide-react';
import ModuleContainer from '../components/layout/ModuleContainer.jsx';
import { usePage } from '../contexts/PageContext.jsx';

// Sprint 22.7.19 (Tier 1 greenfield, Module 9): Narrative Divergence Detector.
// Surfaces GET /api/narrative (narrativeService.buildNarrativeDivergence): per-asset
// media sentiment (Claude-classified news direction) vs price reality (24h move),
// flagging where the tape disagrees with the narrative.

const RED = '#ef4444';
const GREEN = '#10b981';
const AMBER = '#f59e0b';
const MUTED = '#6b7280';

function dirColor(d) {
  if (d === 'bullish') return GREEN;
  if (d === 'bearish') return RED;
  return MUTED;
}

function fmtPct(p) {
  if (p == null) return 'n/a';
  return `${p > 0 ? '+' : ''}${p}%`;
}

export default function NarrativeDivergence() {
  const { setPage } = usePage();
  useEffect(() => { setPage('narrative'); }, []);

  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      const r = await fetch('/api/narrative');
      const d = await r.json();
      if (!r.ok) { setErr(d.error || `HTTP ${r.status}`); return; }
      setData(d);
    } catch (e) { setErr(e.message); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    load();
    const iv = setInterval(load, 120000);
    return () => clearInterval(iv);
  }, [load]);

  const assets = data?.assets || [];
  const divergent = assets.filter(a => a.divergent);
  const aligned = assets.filter(a => !a.divergent);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3 flex-wrap">
        <Split className="w-6 h-6 text-korinth-accent" />
        <h1 className="text-2xl font-bold text-white">Narrative Divergence</h1>
        <span className="text-korinth-muted text-sm">Where media sentiment disagrees with price reality (last {data?.window_hours ?? 24}h)</span>
        <button
          onClick={load}
          className="ml-auto flex items-center gap-1.5 text-[11px] font-mono px-2.5 py-1.5 rounded border border-korinth-border/40 text-korinth-text hover:bg-korinth-border/40 transition-colors"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {err && <div className="text-xs text-red-400 font-mono">{err}</div>}
      {data?.note && <div className="text-[11px] text-korinth-muted">{data.note}</div>}

      {/* 22.7.61: GDELT world tone - the second media-sentiment source */}
      {data?.gdelt_tone?.available && (
        <div className="flex items-center gap-3 flex-wrap text-[11px] font-mono rounded-lg border border-korinth-border/40 px-3 py-2">
          <span className="text-korinth-muted">GDELT world tone (24h, article-weighted):</span>
          <span className="text-white">{data.gdelt_tone.tone_24h}</span>
          <span className="text-korinth-muted">vs 30d {data.gdelt_tone.baseline_30d}</span>
          <span style={{ color: data.gdelt_tone.direction === 'improving' ? '#4ade80' : data.gdelt_tone.direction === 'deteriorating' ? '#f87171' : '#9ca3af' }}>
            {data.gdelt_tone.direction} ({data.gdelt_tone.delta > 0 ? '+' : ''}{data.gdelt_tone.delta})
          </span>
          <span className="text-korinth-muted ml-auto">{(data.gdelt_tone.events_24h || 0).toLocaleString()} events</span>
        </div>
      )}

      {/* Divergences */}
      <ModuleContainer
        title={`Active divergences (${divergent.length})`}
        subtitle="Media and the tape are pointing opposite ways - contrarian / wall-of-worry signals"
      >
        {divergent.length === 0 ? (
          <div className="text-[11px] text-korinth-muted">{loading ? 'Loading...' : 'No narrative divergences right now - media and price are aligned on the priceable asset classes.'}</div>
        ) : (
          <div className="space-y-3">
            {divergent.map((a) => (
              <div
                key={a.symbol}
                className="rounded-lg border p-3"
                style={{ borderColor: `${AMBER}55`, backgroundColor: `${AMBER}0f` }}
              >
                <div className="flex items-center gap-3 flex-wrap">
                  <AlertTriangle className="w-4 h-4" style={{ color: AMBER }} />
                  <span className="text-sm font-bold text-white">{a.symbol}</span>
                  <span className="text-[10px] font-mono px-1.5 py-0.5 rounded" style={{ color: AMBER, backgroundColor: `${AMBER}1f` }}>
                    score {a.divergence_score}/100
                  </span>
                  <span className="text-[11px] font-mono" style={{ color: dirColor(a.news_direction) }}>
                    media {a.news_direction} ({a.news_dominance_pct}% of {a.headlines})
                  </span>
                  <span className="text-[11px] font-mono flex items-center gap-1" style={{ color: dirColor(a.price_direction) }}>
                    {a.price_change_pct_24h > 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                    price {fmtPct(a.price_change_pct_24h)}
                  </span>
                </div>
                <div className="text-[11px] text-korinth-text mt-1.5">{a.interpretation}</div>
                {a.gdelt_corroboration && (
                  <div className="text-[10px] font-mono mt-1" style={{ color: a.gdelt_corroboration === 'corroborates_media' ? '#fbbf24' : '#60a5fa' }}>
                    GDELT tone {a.gdelt_corroboration === 'corroborates_media' ? 'corroborates the media view' : 'contradicts the media view'}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </ModuleContainer>

      {/* All tracked (incl. aligned) */}
      <ModuleContainer title="All tracked asset classes" subtitle="Media direction vs 24h price move, including where they agree">
        {assets.length === 0 ? (
          <div className="text-[11px] text-korinth-muted">{loading ? 'Loading...' : 'No data.'}</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-[11px] font-mono">
              <thead>
                <tr className="text-korinth-muted border-b border-korinth-border/30">
                  <th className="text-left py-1.5 px-2">Symbol</th>
                  <th className="text-left py-1.5 px-2">News classes</th>
                  <th className="text-left py-1.5 px-2">Media</th>
                  <th className="text-right py-1.5 px-2">Headlines</th>
                  <th className="text-right py-1.5 px-2">Price 24h</th>
                  <th className="text-left py-1.5 px-2">Verdict</th>
                </tr>
              </thead>
              <tbody>
                {[...divergent, ...aligned].map((a) => (
                  <tr key={a.symbol} className="border-b border-korinth-border/10 hover:bg-korinth-bg/50">
                    <td className="py-1.5 px-2 text-white">{a.symbol}</td>
                    <td className="py-1.5 px-2 text-korinth-muted">{(a.news_classes || []).join(', ').toLowerCase()}</td>
                    <td className="py-1.5 px-2" style={{ color: dirColor(a.news_direction) }}>
                      {a.news_direction === 'none' ? '-' : `${a.news_direction} ${a.news_dominance_pct}%`}
                    </td>
                    <td className="py-1.5 px-2 text-right text-korinth-text">{a.bullish}/{a.bearish}</td>
                    <td className="py-1.5 px-2 text-right" style={{ color: dirColor(a.price_direction) }}>{fmtPct(a.price_change_pct_24h)}</td>
                    <td className="py-1.5 px-2">
                      {a.divergent
                        ? <span style={{ color: AMBER }}>DIVERGENT</span>
                        : a.news_direction === 'none'
                          ? <span className="text-korinth-muted">no media</span>
                          : <span className="text-korinth-muted">aligned</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </ModuleContainer>

      {data?.as_of && (
        <div className="text-[10px] text-korinth-muted font-mono">As of {new Date(data.as_of).toLocaleString()} - auto-refresh 120s</div>
      )}
    </div>
  );
}
